import { type RPPDomain } from '@retailer-platform/dashboard/core'
import {
  NavEntryPoint,
  NavItem,
  AdminNavEntryPoint,
  AdminNavEntryPointV2,
} from '@retailer-platform/dashboard/routing'
import { storefrontRouter } from './routing/storefrontRouter'
import {
  marketplaceOnlyPageBuilderViewAccessControl,
  pageBuilderViewAccessControl,
  storeConfigurationDraftViewAccessControl,
  slotManagerViewAccessControl,
  storefrontHomepageDashboardV2,
} from './access-control/storefrontAccess.configuration'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import ES_ES from './translations/es-ES.json'
import FR_FR from './translations/fr-FR.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { autosuggestViewAccessControl } from './access-control/autosuggestAccess.configuration'
import {
  departmentNavigationAccessControl,
  navigationViewAccessControl,
} from './access-control/navigationAccess.configuration'
import { navigationAdminViewAccessControl } from './access-control/storefrontAdminAccess.configuration'
import { storefrontAdminRouter } from './routing/storefrontAdminRouter'
import { storefrontPartnerScopedRouter } from './routing/storefrontPartnerScopedRouter'
import { creativeManagerV2AccessControl } from './access-control/creativeManagerAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'storefront',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'storefront-dashboard-tool',
  },
  routes,
  translations: { EN_US, ES_ES, FR_FR },
  routers: {
    admin: [storefrontAdminRouter],
    scoped: [storefrontRouter],
    partnerScoped: [storefrontPartnerScopedRouter],
  },
  navigationEntries: [
    {
      attachTo: NavItem.Storefront,
      subNavItems: [
        {
          type: 'item',
          labelId: 'storefrontDomain.navigation.page-title',
          route: 'storefront-navigation-header',
          accessControl: navigationViewAccessControl,
          position: 0,
        },
      ],
    },
    {
      attachTo: NavItem.Storefront,
      attachToV2: NavEntryPoint.Marketing,
      subNavItems: [
        {
          type: 'item',
          labelId: 'storefrontDomain.slotManager.pageTitle',
          route: 'storefront-slots-manager',
          accessControl: slotManagerViewAccessControl,
          position: 10,
          positionNavV2: 10,
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.creativeManagerV2.nav',
          route: 'storefront-creative-manager-v2',
          accessControl: creativeManagerV2AccessControl,
          position: 8,
          positionNavV2: 3,
          inToolPermissions: [
            {
              slug: Permission.CreativeManagerModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CreativeManagerModify,
            },
          ],
        },
      ],
    },
    {
      attachTo: NavItem.Storefront,
      attachToV2: NavEntryPoint.Merchandising,
      subNavItems: [
        {
          type: 'item',
          labelId: 'storefrontDomain.brand-settings.page-title',
          route: 'storefront-brand-settings',
          subRoutes: ['storefront-custom-images', 'storefront-banner-logo'],
          accessControl: storeConfigurationDraftViewAccessControl,
          position: 2,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.StoreConfigurationDraftModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftModify,
            },
          ],
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.contentPages.dashboard.pageTitle',
          route: 'storefront-content-pages-dashboard',
          accessControl: pageBuilderViewAccessControl,
          position: 3,
          positionNavV2: 5,
          inToolPermissions: [
            {
              slug: Permission.StorefrontPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderModify,
            },
          ],
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.homepage.dashboardV2.pageTitle',
          route: 'storefront-homepage-dashboard-v2',
          accessControl: storefrontHomepageDashboardV2,
          position: 4,
          positionNavV2: 6,
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.autosuggest-links-list.page-title',
          route: 'storefront-autosuggest-links-list',
          accessControl: autosuggestViewAccessControl,
          position: 6,
          positionNavV2: 7,
          inToolPermissions: [
            {
              slug: Permission.AutosuggestModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.AutosuggestModify,
            },
          ],
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.contentPages.dashboard.pageTitle.marketplace',
          route: 'storefront-content-pages-dashboard',
          accessControl: marketplaceOnlyPageBuilderViewAccessControl,
          position: 7,
          positionNavV2: 8,
          inToolPermissions: [
            {
              slug: Permission.StoreConfigsView,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.StoreConfigsView,
            },
            {
              slug: Permission.StorefrontPageBuilderView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderView,
            },
            {
              slug: Permission.StoreConfigsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.StoreConfigsModify,
            },
            {
              slug: Permission.StorefrontPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderModify,
            },
            {
              slug: Permission.ReadOnly,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.ReadOnly,
            },
            {
              slug: Permission.StoreConfigurationDraftView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftView,
            },
            {
              slug: Permission.StoreConfigurationDraftModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftModify,
            },
            {
              slug: Permission.StorefrontRetailerFundedMarketingView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.StorefrontRetailerFundedMarketingView,
            },
            {
              slug: Permission.AdminPanel,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.AdminPanel,
            },
            {
              slug: Permission.StorefrontRetailerFundedMarketingModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.StorefrontRetailerFundedMarketingModify,
            },
          ],
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.navigation.collections.title',
          route: 'storefront-navigation-collections',
          accessControl: departmentNavigationAccessControl,
          position: 1,
          positionNavV2: 1,
          inToolPermissions: [],
        },
        {
          type: 'item',
          labelId: 'storefrontDomain.navigation.navigation.legacy.title',
          route: 'storefront-navigation-header',
          accessControl: navigationViewAccessControl,
          positionNavV2: 3,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachTo: AdminNavEntryPoint.LandingPageSystem,
      attachToV2: AdminNavEntryPointV2.Merchandising,
      subNavItems: [
        {
          route: 'storefront-admin-navigation-footer',
          labelId: 'storefrontDomain.navigation.footer.mp.nav.title',
          accessControl: navigationAdminViewAccessControl,
          positionNavV2: 0,
        },
      ],
    },
  ],
}

export default definition
